import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  Loader2,
  Mail,
  MessageSquareWarning,
  Receipt,
  Trash2,
  UserX,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { formatDate, formatMoney, formatPercent, formatPeriod } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [invoiceNote, setInvoiceNote] = useState("");
  const [invoiceRequests, setInvoiceRequests] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/${id}`);
      setData(r.data);
      const ir = await api.get(`/reports/${id}/invoice-requests`);
      setInvoiceRequests(ir.data);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleRequestInvoice = async () => {
    setSubmitting(true);
    try {
      await api.post(`/reports/${id}/request-invoice`, { extra_note: invoiceNote || undefined });
      toast.success("Invoice-Request gesendet");
      setInvoiceOpen(false);
      setInvoiceNote("");
      const ir = await api.get(`/reports/${id}/invoice-requests`);
      setInvoiceRequests(ir.data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Report wirklich löschen?")) return;
    try {
      await api.delete(`/reports/${id}`);
      toast.success("Report gelöscht");
      navigate("/reports");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Report …
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="p-8 text-rose-600">
        {error || "Report nicht gefunden."}
      </div>
    );
  }

  const { report, entries, missing_customers: missing } = data;
  const discrepancies = entries.filter((e) => e.has_discrepancy);

  return (
    <div data-testid="report-detail-page">
      <PageHeader
        title={`Report ${report.po_number}`}
        subtitle={formatPeriod(report.period_month, report.period_label)}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => setClaimOpen(true)}
              data-testid="report-claim-btn"
              className="rounded-sm"
            >
              <MessageSquareWarning size={16} className="mr-2" /> Report Claim
            </Button>
            <Button
              variant="outline"
              onClick={() => setInvoiceOpen(true)}
              data-testid="request-invoice-btn"
              className="rounded-sm"
            >
              <Receipt size={16} className="mr-2" /> Request Invoice
            </Button>
            <Button
              onClick={handleDelete}
              variant="outline"
              data-testid="delete-report-btn"
              className="rounded-sm text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            >
              <Trash2 size={16} />
            </Button>
          </>
        }
      >
        <Link
          to="/reports"
          className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 text-sm"
        >
          <ArrowLeft size={14} /> Zurück zur Übersicht
        </Link>
      </PageHeader>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 space-y-6">
        {/* Quick stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Stat label="Typ"><StatusBadge status={report.type} /></Stat>
          <Stat label="Provision" mono>{formatMoney(report.total_commission)}</Stat>
          <Stat label="Einträge" mono>{report.entry_count}</Stat>
          <Stat label="Kunden" mono>{report.customer_count}</Stat>
          <Stat label="Abweichungen" mono>
            {report.discrepancy_count > 0 ? (
              <span className="text-rose-700">{report.discrepancy_count}</span>
            ) : (
              "0"
            )}
          </Stat>
        </div>

        {missing.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-5" data-testid="missing-customers-warning">
            <div className="flex items-center gap-2 mb-2 text-amber-800">
              <UserX size={18} />
              <h3 className="font-display font-bold">
                {missing.length} fehlende{missing.length === 1 ? "r" : ""} Kunde{missing.length === 1 ? "" : "n"} im Report
              </h3>
            </div>
            <div className="text-sm text-amber-900 mb-3">
              Aktive Kunden mit erfasster {report.type === "payment" ? "Payment" : "Lizenz"}-Provision in Vor-Monaten, die nicht im Report enthalten sind:
            </div>
            <div className="flex flex-wrap gap-2">
              {missing.map((m) => (
                <Link
                  key={m.customer_id}
                  to={`/customers/${m.customer_id}`}
                  className="inline-flex items-center gap-1 bg-white border border-amber-300 rounded-sm px-3 py-1 text-sm hover:bg-amber-100"
                  data-testid={`missing-customer-${m.customer_id}`}
                >
                  {m.customer_name} <ChevronRight size={12} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {invoiceRequests.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="invoice-requests-block">
            <div className="kbd-label mb-2 flex items-center gap-1.5">
              <Mail size={12} /> Bisherige Invoice-Requests
            </div>
            <div className="space-y-2">
              {invoiceRequests.map((r) => (
                <div key={r.id} className="flex items-center gap-3 text-sm">
                  <StatusBadge status={r.status} />
                  <span className="font-mono">{formatMoney(r.amount)}</span>
                  <span className="text-slate-500">→ {r.recipients.join(", ")}</span>
                  <span className="text-slate-400">{formatDate(r.sent_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Tabs defaultValue="entries" className="w-full">
          <TabsList className="bg-white border border-slate-200 rounded-sm">
            <TabsTrigger value="entries" data-testid="tab-entries">
              Einträge ({entries.length})
            </TabsTrigger>
            <TabsTrigger value="discrepancies" data-testid="tab-discrepancies">
              Abweichungen ({discrepancies.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="entries">
            <EntriesTable entries={entries} reportType={report.type} />
          </TabsContent>
          <TabsContent value="discrepancies">
            {discrepancies.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-slate-500">
                Keine Abweichungen erfasst.
              </div>
            ) : (
              <EntriesTable entries={discrepancies} reportType={report.type} />
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent data-testid="invoice-request-dialog">
          <DialogHeader>
            <DialogTitle>Invoice anfordern</DialogTitle>
            <DialogDescription>
              Sendet die Aufforderung „Please invoice {report.po_number} with the amount{" "}
              {formatMoney(report.total_commission)} to Lightspeed“ an die in den Einstellungen
              konfigurierten Empfänger.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="note">Optional: zusätzliche Notiz</Label>
            <Textarea
              id="note"
              value={invoiceNote}
              onChange={(e) => setInvoiceNote(e.target.value)}
              placeholder="z.B. „Vorrang vor anderen offenen Rechnungen“"
              data-testid="invoice-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleRequestInvoice}
              disabled={submitting}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="invoice-send-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClaimDialog
        open={claimOpen}
        onOpenChange={setClaimOpen}
        report={report}
      />
    </div>
  );
}

function Stat({ label, children, mono }) {
  return (
    <div className="kpi-card p-4 rounded-sm">
      <div className="kbd-label mb-1.5">{label}</div>
      <div className={mono ? "font-mono font-semibold text-lg" : "text-lg"}>{children}</div>
    </div>
  );
}

function EntriesTable({ entries, reportType }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mt-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <th className="text-left px-4 py-3 font-semibold">Kunde</th>
              {reportType === "license" && (
                <>
                  <th className="text-left px-4 py-3 font-semibold">Trans. Typ</th>
                  <th className="text-left px-4 py-3 font-semibold">Invoice-Line</th>
                  <th className="text-left px-4 py-3 font-semibold">Trans. Datum</th>
                </>
              )}
              <th className="text-right px-4 py-3 font-semibold">Basis</th>
              <th className="text-right px-4 py-3 font-semibold">Provision</th>
              <th className="text-right px-4 py-3 font-semibold">Satz</th>
              <th className="text-right px-4 py-3 font-semibold">Soll</th>
              <th className="text-right px-4 py-3 font-semibold">Diff.</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr
                key={e.id}
                className={`border-b border-slate-100 ${e.has_discrepancy ? "discrepancy-row" : "hover:bg-slate-50"}`}
                data-testid={`entry-row-${e.id}`}
              >
                <td className="px-4 py-3 font-medium text-slate-900">
                  {e.customer_id ? (
                    <Link
                      to={`/customers/${e.customer_id}`}
                      className="hover:underline underline-offset-2"
                    >
                      {e.customer_name}
                    </Link>
                  ) : (
                    e.customer_raw_name || "—"
                  )}
                  {e.customer_auto_created && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-blue-600 font-semibold">
                      neu
                    </span>
                  )}
                </td>
                {reportType === "license" && (
                  <>
                    <td className="px-4 py-3 text-slate-600">{e.transaction_type || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {e.customer_invoice_id || "—"}
                      {e.customer_invoice_line_id && (
                        <div className="text-slate-400 mt-0.5">
                          {e.customer_invoice_line_id}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{formatDate(e.transaction_date)}</td>
                  </>
                )}
                <td className="px-4 py-3 text-right font-mono">
                  {formatMoney(e.commissionable_amount)}
                </td>
                <td className="px-4 py-3 text-right font-mono font-semibold">
                  {formatMoney(e.commission_amount)}
                </td>
                <td className="px-4 py-3 text-right font-mono">{formatPercent(e.commission_rate)}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-500">
                  {e.expected_amount != null ? formatMoney(e.expected_amount) : "—"}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {e.has_discrepancy ? (
                    <span className="text-rose-700 font-semibold">
                      {formatMoney(e.discrepancy_amount, true)}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ClaimDialog({ open, onOpenChange, report }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setSubject(`Korrekturanfrage zu Report ${report.po_number} (${formatPeriod(report.period_month, report.period_label)})`);
      setBody(
        `Hallo Karim,\n\nbitte um Überprüfung des Reports ${report.po_number} (${formatPeriod(report.period_month, report.period_label)}).\n\nFolgender Punkt scheint nicht zu stimmen:\n\n— Bitte hier die Details eintragen —\n\nDanke und beste Grüße,\nGND Gastro Next Digital UG`
      );
    }
  }, [open, report]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/claims", {
        subject,
        body,
        related_report_id: report.id,
      });
      toast.success("Claim erstellt und E-Mail gesendet");
      onOpenChange(false);
      navigate(`/claims/${r.data.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="claim-dialog">
        <DialogHeader>
          <DialogTitle>Report Claim eröffnen</DialogTitle>
          <DialogDescription>
            Sendet eine Reklamation an den Lightspeed Partner Consultant. Antworten werden im Dialog
            automatisch eingesammelt (sofern IMAP konfiguriert ist).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="subject">Betreff</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="claim-subject-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="body">Nachricht</Label>
            <Textarea
              id="body"
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="claim-body-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="claim-send-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Senden &amp; Dialog eröffnen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
